import { message } from "antd"
import {
  ErrorNotification,
  SuccessNotification,
} from "./NotificationTemplate.component"

/**
 * Unified user feedback API — the one import for toasts and notifications.
 *
 * Rules of thumb:
 * - `notify.success` / `notify.error`: rich notifications with title +
 *   description, for operation outcomes (saves, deletes, failures).
 * - `notify.toast.*`: lightweight inline toasts for transient feedback
 *   (copy-to-clipboard, download started, minor validation nudges).
 *
 * Prefer this over importing antd `message`/`notification` or the template
 * functions directly — call sites currently mix all three with inconsistent
 * durations and phrasing. New code should use `notify`; old call sites
 * migrate opportunistically when touched.
 *
 * Works outside components (react-query mutation callbacks, services) —
 * antd statics are patched for React 19 at the app entry.
 */
export const notify = {
  /** Operation succeeded — rich notification with title + description. */
  success(title: string, description: string = ""): void {
    SuccessNotification(title, description)
  },

  /** Operation failed — rich notification with title + description. */
  error(title: string, description: string = ""): void {
    ErrorNotification(title, description)
  },

  /**
   * Lightweight transient toasts.
   *
   * `duration` is seconds before auto-dismiss; omit it for the antd default.
   */
  toast: {
    success(text: string, duration?: number): void {
      void message.success(text, duration)
    },
    error(text: string, duration?: number): void {
      void message.error(text, duration)
    },
    info(text: string, duration?: number): void {
      void message.info(text, duration)
    },
    warning(text: string, duration?: number): void {
      void message.warning(text, duration)
    },
    /**
     * Shows a loading toast; returns a function that dismisses it.
     * @example const done = notify.toast.loading("Downloading…"); …; done()
     */
    loading(text: string): () => void {
      return message.loading(text, 0)
    },
  },
}
